import type { Pinia } from 'pinia';
import * as Vue from 'vue';
import { bindDesignerHostCommands, installVeyopRuntime } from '@core/veyopRuntime';

type VeyopRuntime = typeof window.Veyop;

let owners = 0;
let previousRuntime: VeyopRuntime;
let templateRuntime: VeyopRuntime;

/**
 * Drop-in addon bundles (text-arc, typography-pro, mask-images, ...) are built with
 * `external: ['vue']` and end in `})(Vue)`, so they need the runtime on `window`.
 * designer-app and admin-app publish it; wp-admin/post.php has neither — only this
 * bundle — so every addon IIFE there died with `ReferenceError: Vue is not defined`
 * before it could register its SVG exporter, and the regenerated product mockup
 * silently fell back to plain, un-arced, un-styled text.
 *
 * Published here rather than at bundle scope so a host that ships its own Vue has
 * already claimed the global by the time addons load — one runtime, no clobbering.
 */
function publishVueRuntime(): void {
  const w = window as unknown as { Vue?: typeof Vue };
  if (!w.Vue) w.Vue = Vue;
}

/**
 * Swap the admin facade for this bundle's designer registries while any
 * template editor is mounted. The owner count handles Vue route overlap
 * (`new` -> `edit`) without the old instance restoring over the new one.
 */
export function acquireTemplateDesignerRuntime(pinia: Pinia): Promise<void> {
  publishVueRuntime();
  if (owners === 0) {
    previousRuntime = window.Veyop;
    installVeyopRuntime();
    templateRuntime = window.Veyop;
  } else if (templateRuntime) {
    window.Veyop = templateRuntime;
  }
  owners += 1;
  return bindDesignerHostCommands(pinia);
}

export function releaseTemplateDesignerRuntime(): void {
  owners = Math.max(0, owners - 1);
  if (owners !== 0) return;
  if (window.Veyop === templateRuntime) {
    window.Veyop = previousRuntime;
  }
  previousRuntime = undefined;
  templateRuntime = undefined;
}
